import { useEffect, useState } from "react";
import { Volume2, Star, Check, X } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { speak } from "@/lib/buddy";

const Choice = ({ text, i, state, onPick }) => {
  const cls = state === "right" ? "bg-[#C8DCCB] border-[#6B9080]" : state === "wrong" ? "bg-[#F6D5C8] border-[#C87D53]" : "bg-white border-[#E6DEC3] hover:border-[#2C4A3E]";
  return (
    <button onClick={() => onPick(i)} disabled={!!state || state === null} data-testid={`word-quiz-choice-${i}`}
      className={`w-full text-left px-5 py-4 rounded-2xl border transition-colors flex items-center gap-3 ${cls}`}>
      <span className="flex-1 text-stone-700">{text}</span>
      {state === "right" && <Check className="w-5 h-5 text-[#2C4A3E]" />}{state === "wrong" && <X className="w-5 h-5 text-[#C87D53]" />}
    </button>
  );
};

const Done = ({ score, total, stars, onAgain, onExit }) => (
  <div className="text-center py-6" data-testid="word-quiz-done">
    <p className="font-display text-3xl text-[#2C4A3E]">You got {score} of {total}!</p>
    {stars > 0 && <p className="mt-2 text-[#C87D53] inline-flex items-center gap-1.5" data-testid="word-quiz-new-stars"><Star className="w-5 h-5 fill-current" /> {stars} new star word{stars > 1 ? "s" : ""}</p>}
    <div className="mt-6 flex justify-center gap-3">
      <button onClick={onAgain} data-testid="word-quiz-again" className="px-6 py-3 rounded-full bg-[#C87D53] text-white">Play again</button>
      <button onClick={onExit} data-testid="word-quiz-exit" className="px-6 py-3 rounded-full border border-[#E6DEC3]">Back to my words</button>
    </div>
  </div>
);

export const WordQuiz = ({ onExit }) => {
  const { buddy } = useProfile();
  const [q, setQ] = useState(null);
  const [i, setI] = useState(0);
  const [res, setRes] = useState(null);
  const [tally, setTally] = useState({ score: 0, stars: 0 });
  const start = () => { setQ(null); setI(0); setRes(null); setTally({ score: 0, stars: 0 });
    http.post("/words/quiz").then((r) => setQ(r.data)).catch((e) => { toast.error(formatApiError(e)); onExit(); }); };
  useEffect(start, []); // eslint-disable-line react-hooks/exhaustive-deps
  const item = q?.items[i];
  const say = () => item && speak(`${item.word}. What does ${item.word} mean?`, buddy?.voice, 0.85).catch(() => {});
  useEffect(() => { say(); }, [item?.word_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!q) return <p className="text-stone-500 py-10" data-testid="word-quiz-loading">{buddy?.name || "Your buddy"} is picking your words…</p>;
  if (i >= q.items.length) return <Done {...tally} total={q.items.length} onAgain={start} onExit={onExit} />;
  const pick = async (c) => {
    try {
      const r = (await http.post(`/words/quiz/${q.id}/answer`, { index: i, choice: c })).data;
      setRes({ ...r, picked: c });
      setTally((t) => ({ score: t.score + r.correct, stars: t.stars + r.mastered }));
      speak(r.correct ? (r.mastered ? `Yes! You earned a star for ${item.word}!` : "Yes, that's right!") : `Good try. ${item.word} means ${item.choices[r.answer]}`, buddy?.voice).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const stateOf = (c) => (!res ? undefined : c === res.answer ? "right" : c === res.picked ? "wrong" : null);
  return (
    <div className="rounded-[2rem] bg-[#F7F3EB] border border-[#E6DEC3] p-6 md:p-8 rise" data-testid="word-quiz">
      <div className="flex items-center gap-4">
        <BuddyAvatar buddy={buddy} className="w-16 h-16 shrink-0" />
        <div className="flex-1"><p className="text-xs uppercase tracking-widest text-stone-500">Word {i + 1} of {q.items.length}{q.review ? " · star review" : ""}</p>
          <p className="font-display text-4xl text-[#2C4A3E]" data-testid="word-quiz-word">{item.syllables}</p></div>
        <button onClick={say} data-testid="word-quiz-say" aria-label="Hear the word" className="p-3 rounded-full bg-white border border-[#E6DEC3]"><Volume2 className="w-5 h-5" /></button>
      </div>
      <div className="mt-6 space-y-3">{item.choices.map((c, k) => <Choice key={k} i={k} text={c} state={stateOf(k)} onPick={pick} />)}</div>
      {res && (
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <p className="flex-1 text-sm text-[#2C4A3E]" data-testid="word-quiz-feedback">{res.mastered ? "Star word! You know this one now." : res.correct ? `Nice! ${res.streak} in a row.` : "Not quite. Let's keep practicing it."}</p>
          <button onClick={() => { setRes(null); setI(i + 1); }} data-testid="word-quiz-next" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-white">{i + 1 < q.items.length ? "Next word" : "Finish"}</button>
        </div>
      )}
    </div>
  );
};
